import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Folder, FolderOpen, FolderPlus, Search, X } from 'lucide-react';
import { formatNumber, normalizeList, sameList } from './dataDictionaryUtils';

const MAX_DEPTH = 8;

function filterTree(nodes, needle) {
  if (!needle) return nodes;
  const n = needle.toLowerCase();
  const walk = (list) =>
    list
      .map((node) => {
        const children = walk(node.children || []);
        const match = node.name.toLowerCase().includes(n);
        if (!match && children.length === 0) return null;
        return { ...node, children, forceOpen: true };
      })
      .filter(Boolean);
  return walk(nodes);
}

function PathLabel({ path, placeholder = 'No folder' }) {
  const parts = normalizeList(path);
  if (!parts.length) return <span className="text-zinc-400">{placeholder}</span>;
  return (
    <span className="flex min-w-0 items-center gap-1 truncate">
      {parts.map((p, i) => (
        <React.Fragment key={`${p}-${i}`}>
          {i > 0 ? <span className="text-zinc-300">/</span> : null}
          <span className={i === parts.length - 1 ? 'truncate text-[#18181B]' : 'shrink-0 text-[#71717A]'}>{p}</span>
        </React.Fragment>
      ))}
    </span>
  );
}

/**
 * Folder path picker: searchable tree of existing folders (from /folders), inline "new subfolder",
 * and "remove from folder". `value` is a string[] path; `onChange(path)` receives the new path ([] = none).
 * `mixedLabel` renders when the current value is ambiguous (bulk edit).
 */
export default function FolderPicker({ value, onChange, folders = [], disabled = false, placeholder = 'No folder', mixedLabel = '' }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());
  const [newParent, setNewParent] = useState(null); // path (string[]) or null
  const [newName, setNewName] = useState('');
  const wrapRef = useRef(null);
  const searchRef = useRef(null);
  const path = useMemo(() => normalizeList(value), [value]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setNewParent(null);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    if (!open) return;
    // expand ancestors of the current value
    setExpanded((prev) => {
      const next = new Set(prev);
      for (let i = 1; i <= path.length; i += 1) next.add(path.slice(0, i).join(' '));
      return next;
    });
    const t = setTimeout(() => searchRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open, path]);

  const toggle = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const visible = useMemo(() => filterTree(folders, query.trim()), [folders, query]);

  const choose = (nextPath) => {
    onChange(normalizeList(nextPath));
    setOpen(false);
    setQuery('');
    setNewParent(null);
    setNewName('');
  };

  const createSubfolder = () => {
    const name = newName.replace(/\//g, ' ').trim();
    if (!name) return;
    const parent = normalizeList(newParent);
    if (parent.length >= MAX_DEPTH) return;
    choose([...parent, name]);
  };

  const renderNodes = (nodes, depth = 0) =>
    nodes.map((node) => {
      const key = node.path.join(' ');
      const hasChildren = (node.children || []).length > 0;
      const isOpen = node.forceOpen || expanded.has(key);
      const selected = sameList(node.path, path);
      return (
        <React.Fragment key={key}>
          <div
            className={`flex h-8 items-center gap-1 rounded-md pr-2 text-[13px] ${selected ? 'bg-primary-50 text-primary-700 font-medium' : 'text-[#3F3F46] hover:bg-[#F3F2EE]'}`}
            style={{ paddingLeft: 6 + depth * 18 }}
          >
            <button
              type="button"
              tabIndex={-1}
              onClick={() => toggle(key)}
              disabled={!hasChildren}
              className="inline-flex h-4 w-4 items-center justify-center text-zinc-400 hover:text-zinc-700 disabled:invisible"
              aria-label={isOpen ? 'Collapse' : 'Expand'}
            >
              {isOpen ? <ChevronDown size={12} aria-hidden /> : <ChevronRight size={12} aria-hidden />}
            </button>
            <button type="button" onClick={() => choose(node.path)} className="flex min-w-0 flex-1 items-center gap-1.5 text-left">
              {isOpen && hasChildren ? <FolderOpen size={15} className="shrink-0 text-zinc-500" aria-hidden /> : <Folder size={15} className="shrink-0 text-zinc-500" aria-hidden />}
              <span className="truncate">{node.name}</span>
              <span className="ml-auto pl-2 text-xs tabular-nums text-zinc-400">{formatNumber(node.total)}</span>
            </button>
            {node.path.length < MAX_DEPTH ? (
              <button
                type="button"
                onClick={() => {
                  setNewParent(node.path);
                  setNewName('');
                  if (!isOpen) toggle(key);
                }}
                className="ml-1 inline-flex h-6 w-6 items-center justify-center rounded text-zinc-400 hover:bg-white hover:text-primary-700"
                aria-label={`New subfolder in ${node.path.join(' / ')}`}
                title="New subfolder"
              >
                <FolderPlus size={14} aria-hidden />
              </button>
            ) : null}
          </div>
          {isOpen && newParent && sameList(newParent, node.path) ? (
            <div className="flex items-center gap-2 py-1 pr-2" style={{ paddingLeft: 30 + depth * 18 }}>
              <input
                type="text"
                value={newName}
                autoFocus
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    createSubfolder();
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    setNewParent(null);
                  }
                }}
                placeholder="New subfolder name"
                aria-label="New subfolder name"
                className="h-7 flex-1 rounded-md border border-hairline px-2 text-[13px] focus:outline-none focus:ring-1 focus:ring-[#18181B]/20"
              />
              <button type="button" onClick={createSubfolder} className="h-7 rounded-md bg-[#0062FB] px-2.5 text-xs font-medium text-white hover:bg-[#0052E0] disabled:opacity-50" disabled={!newName.trim()}>
                Create
              </button>
            </div>
          ) : null}
          {isOpen && hasChildren ? renderNodes(node.children, depth + 1) : null}
        </React.Fragment>
      );
    });

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        id={id}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="tree"
        aria-expanded={open}
        className={`flex h-[38px] w-full items-center gap-2 rounded-lg border bg-surface px-3 text-left text-sm transition-colors disabled:opacity-60 ${
          open ? 'border-[#18181B]/40' : 'border-hairline hover:border-zinc-300'
        }`}
      >
        <Folder size={16} className="shrink-0 text-zinc-500" aria-hidden />
        {mixedLabel ? <span className="truncate italic text-[#71717A]">{mixedLabel}</span> : <PathLabel path={path} placeholder={placeholder} />}
        <ChevronDown size={14} className="ml-auto shrink-0 text-zinc-500" aria-hidden />
      </button>

      {open && !disabled ? (
        <div className="absolute left-0 right-0 z-30 mt-1 flex max-h-[26rem] flex-col rounded-xl border border-hairline bg-white p-2 shadow-[0_12px_32px_rgba(24,24,27,0.12),0_2px_6px_rgba(24,24,27,0.06)]">
          <label className="relative mb-1.5 block">
            <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" aria-hidden />
            <input
              ref={searchRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  setOpen(false);
                }
              }}
              placeholder="Find a folder…"
              aria-label="Find a folder"
              className="h-8 w-full rounded-md border border-hairline pl-8 pr-2 text-[13px] focus:outline-none focus:ring-1 focus:ring-[#18181B]/10"
            />
          </label>
          <div className="min-h-0 flex-1 overflow-y-auto" role="tree">
            {visible.length ? renderNodes(visible) : <p className="px-2 py-3 text-xs text-zinc-500">No folders match.</p>}
          </div>
          <div className="mt-1.5 border-t border-hairline pt-1.5">
            {newParent && newParent.length === 0 ? (
              <div className="flex items-center gap-2 px-1 py-1">
                <input
                  type="text"
                  value={newName}
                  autoFocus
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      createSubfolder();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      setNewParent(null);
                    }
                  }}
                  placeholder="New top-level folder"
                  aria-label="New top-level folder name"
                  className="h-7 flex-1 rounded-md border border-hairline px-2 text-[13px] focus:outline-none focus:ring-1 focus:ring-[#18181B]/20"
                />
                <button type="button" onClick={createSubfolder} className="h-7 rounded-md bg-[#0062FB] px-2.5 text-xs font-medium text-white hover:bg-[#0052E0] disabled:opacity-50" disabled={!newName.trim()}>
                  Create
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setNewParent([]);
                  setNewName('');
                }}
                className="flex h-8 w-full items-center gap-2 rounded-md px-2 text-[13px] text-primary-700 hover:bg-[#F3F2EE]"
              >
                <FolderPlus size={14} aria-hidden /> New top-level folder
              </button>
            )}
            {path.length ? (
              <button type="button" onClick={() => choose([])} className="flex h-8 w-full items-center gap-2 rounded-md px-2 text-[13px] text-[#71717A] hover:bg-[#F3F2EE]">
                <X size={14} aria-hidden /> Remove from folder
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export { PathLabel };
